import { useTranslation } from 'react-i18next';
import { PipelineStatusBadge } from './PipelineStatusBadge.js';
import { PipelineTypeBadge } from './PipelineTypeBadge.js';
import type { PipelineConfig } from '../../../types/api.js';

export interface PipelineListTableProps {
  /** パイプライン設定一覧 */
  configs: PipelineConfig[];
  /** 行選択ハンドラー */
  onSelect: (config: PipelineConfig) => void;
  /** 手動実行ハンドラー */
  onTrigger: (config: PipelineConfig) => void;
}

/**
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business パイプライン一覧テーブル。ジョブ名・タイプ・ステータス・Cron・対象DBを表示し行選択・実行ボタンを提供
 * @graph-connects PipelineStatusBadge [calls] ステータスバッジ描画
 * @graph-connects PipelineTypeBadge [calls] タイプバッジ描画
 */
export function PipelineListTable({ configs, onSelect, onTrigger }: PipelineListTableProps) {
  const { t } = useTranslation();

  return (
    <div className="glass-card overflow-hidden">
      {/* モバイル: カード表示 */}
      <div className="block md:hidden">
        {configs.map((config, index) => (
          <div
            key={config.id}
            className="stagger-item p-4 border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--glass-bg-hover)] transition-colors cursor-pointer"
            style={{ '--stagger-index': index } as React.CSSProperties}
            onClick={() => {
              onSelect(config);
            }}
            role="button"
            tabIndex={0}
            aria-label={`${t('pipeline.detail')}: ${config.jobName}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(config);
              }
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[var(--color-text-primary)] truncate">
                {config.jobName}
              </span>
              <PipelineStatusBadge status={config.status} />
            </div>
            <div className="flex items-center gap-2 mb-2">
              <PipelineTypeBadge type={config.type} />
              {config.targetDb && (
                <span className="text-xs text-[var(--color-text-muted)]">{config.targetDb}</span>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-[var(--color-text-muted)] font-mono">
                {config.cronExpression}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTrigger(config);
                }}
                className="px-3 py-1 rounded-md text-xs font-medium bg-[var(--color-accent-primary)] text-white hover:bg-[var(--color-accent-hover)] transition-colors"
                aria-label={`${t('pipeline.trigger')}: ${config.jobName}`}
              >
                {t('pipeline.trigger')}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* デスクトップ: テーブル表示 */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="text-left py-3 px-4 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
                {t('pipeline.jobName')}
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
                {t('pipeline.type')}
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
                {t('pipeline.status')}
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
                {t('pipeline.cron')}
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
                {t('pipeline.targetDb')}
              </th>
              <th className="text-right py-3 px-4 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
                {/* アクション列 */}
              </th>
            </tr>
          </thead>
          <tbody>
            {configs.map((config, index) => (
              <tr
                key={config.id}
                className="stagger-item border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--glass-bg-hover)] transition-colors cursor-pointer"
                style={{ '--stagger-index': index } as React.CSSProperties}
                onClick={() => {
                  onSelect(config);
                }}
                role="button"
                tabIndex={0}
                aria-label={`${t('pipeline.detail')}: ${config.jobName}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(config);
                  }
                }}
              >
                <td className="py-3 px-4 text-sm text-[var(--color-text-primary)] font-medium">
                  {config.jobName}
                </td>
                <td className="py-3 px-4">
                  <PipelineTypeBadge type={config.type} />
                </td>
                <td className="py-3 px-4">
                  <PipelineStatusBadge status={config.status} />
                </td>
                <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] font-mono">
                  {config.cronExpression}
                </td>
                <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)]">
                  {config.targetDb ?? '-'}
                </td>
                <td className="py-3 px-4 text-right">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onTrigger(config);
                    }}
                    className="px-3 py-1.5 rounded-md text-xs font-medium bg-[var(--color-accent-primary)] text-white hover:bg-[var(--color-accent-hover)] transition-colors"
                    aria-label={`${t('pipeline.trigger')}: ${config.jobName}`}
                  >
                    {t('pipeline.trigger')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
